import type { ReconciliationRowVO, ReconciliationRowFieldsVO } from '../../../apis/types'
import { fieldColumns, displayString } from './reconciliation.types'

const escapeCell = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`
    }
    return value
}

const formatCellValue = (value: string | number | boolean | null | undefined): string => {
    if (value == null) return ''
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    return String(value)
}

const formatDateValue = (value: string | number | boolean | null | undefined): string => {
    if (value == null || typeof value !== 'string') return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function buildCsvContent(rows: ReconciliationRowVO[]): string {
    const headers = [
        'Account Name',
        'Utility Account',
        'OneBill Account',
        'Meter Number',
        'Billing Mode',
        'Row Status',
        'Issues',
    ]

    for (const col of fieldColumns) {
        if (col.fieldKey === 'lastPaymentDate') {
            headers.push('Last Payment Date', 'Last Payment Amount')
            continue
        }
        headers.push(col.label)
    }

    headers.push('Has Mismatch', 'Mismatch Details')

    const csvRows = [headers.map(escapeCell).join(',')]
    const dateFields = new Set(['lastPaymentDate', 'lastStmtDueDate', 'lastStmtDate'])

    for (const row of rows) {
        const cells: string[] = [
            escapeCell(displayString(row.fields.accountName) ?? ''),
            escapeCell(row.utilityAccountNumber),
            escapeCell(row.onebillAccountNumber),
            escapeCell(row.meterNumber ?? ''),
            escapeCell(row.billingMode ?? ''),
            escapeCell(row.rowStatus),
            escapeCell(row.issues.join('; ')),
        ]

        const mismatchDetails: string[] = []

        for (const col of fieldColumns) {
            const field = row.fields[col.fieldKey as keyof ReconciliationRowFieldsVO]
            const formatter = dateFields.has(col.fieldKey) ? formatDateValue : formatCellValue

            if (col.fieldKey === 'lastPaymentDate') {
                cells.push(escapeCell(formatter(field.display)))
                cells.push(escapeCell(formatCellValue(row.fields.lastPaymentAmount.display)))
            } else {
                cells.push(escapeCell(formatter(field.display)))
            }

            if (field.status === 'mismatch' && field.mismatch) {
                mismatchDetails.push(
                    `${col.label}: ${formatCellValue(field.mismatch.authoritativeValue)} vs ${formatCellValue(field.mismatch.comparisonValue)}`
                )
            }
        }

        cells.push(escapeCell(mismatchDetails.length > 0 ? 'Yes' : 'No'))
        cells.push(escapeCell(mismatchDetails.join('; ')))

        csvRows.push(cells.join(','))
    }

    return csvRows.join('\n')
}

export function downloadCsv(rows: ReconciliationRowVO[], propertyName: string): void {
    const csvContent = buildCsvContent(rows)
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${propertyName} - Reconciliation Report.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}
